import { observer } from "mobx-react";
import React, { ReactNode } from "react";
import Box from "react-raster";
import { theme } from "src/config";
import { DataType } from "src/types";
import { HSVtoRGB, hexToRGB, RGBToHex, RGBtoHSV } from "src/util/util";
import styled from "styled-components";
import { StackCard } from "./stackCard";
import { Text } from "src/components";

export interface ICardProps {
  expanded?:        boolean;
  height?:          string;
  width?:           string;
  editable?:        boolean;
  footer?:          ReactNode;
  body:             ReactNode;
  expandedBody:     ReactNode;
  backgroundColor?: string;
  action?:          ReactNode;
  grayOut?:         boolean;
  stack?:           boolean;
  onAction?(e: React.MouseEvent<HTMLDivElement, MouseEvent>): void;
  onClick? (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void;
  onDrop?():   void;
  onChange?():   void;
  dropFilter?: DataType[];
  enableDropZone?: boolean;
  isDragging?: boolean;
  isOver?: boolean;
}

const Body = styled.div<{expanded: boolean, padding?: string}>`
  display:         flex;
  padding:         ${props => props.padding || '0.8rem'};
  /* padding-bottom:  0.45rem; */
  flex-direction:  column;
  justify-content: space-between;
  width:           100%;
  opacity:         ${props => props.expanded ? 0 : 1};
  transition:      opacity 0.1s ease-in-out;
`;

const Container = styled.div<{hoverColor: string, expanded: boolean, grayOut: boolean, height: string, width: string, isOver: boolean, isDragging: boolean}>`
  display:         flex;
  background:      ${props => props.isOver ? (props.hoverColor  || "rgba(166, 195, 209, 1)" ) : props.color || theme.color.white};
  border:          1px solid ${theme.color.primaryBorder};
  border-radius:   4px;
  position:        relative;
  height:          ${props => props.expanded ? props.height : "12rem"};
  width:           ${props => props.expanded ? props.width : "22rem"};
  opacity:         ${props => props.grayOut ? 0.4 : 1 };
  transition-property:        height, width;
  transition-duration:        0.2s;
  transition-timing-function: ease-in-out;


  :hover {
    /* transform:  ${props => props.expanded ? 'scale(1)' : 'scale(1.1)'}; */
    cursor:     ${props => props.expanded ? undefined : "pointer"};
    /* transition: transform 0.1s ease-in-out; */
  }
`;

const Footer = styled.div<{borderColor: string}>`
  border-top:   ${props => (props.borderColor || theme.color.primary6) + " solid 1px"};
  padding-top:  0.45rem;
  padding-left: 0.45rem;
  display:      flex;
  width:        100%;
`;

const ActionContainer = styled.div`
  position:        absolute;
  display:         flex;
  padding:         0.2rem;
  justify-content: center;
  align-items:     center;
  top:             -1.35rem;
  right:           -0.75rem;
  margin-right:    0;
  z-index:         1;
`;

const PosContainer = styled.div`
  position: relative;
  z-index: 0;
`;


export const Card = observer(React.forwardRef((props: ICardProps, ref: React.Ref<HTMLDivElement>): JSX.Element => {
  const { 
    expanded,
    editable,
    footer,
    body,
    expandedBody,
    backgroundColor,
    action,
    onAction,
    onClick,
    grayOut,
    height,
    width, 
    isDragging,
    isOver,
  } = props;
  let hoverColor = React.useMemo(() => {
    if(!backgroundColor) return undefined;

    const hsv = RGBtoHSV(hexToRGB(backgroundColor));
    hsv[2] = hsv[2] * 0.75;
    return RGBToHex(HSVtoRGB(hsv));
  }, [backgroundColor]);

  const exp = expanded && editable;

  return (
    <PosContainer className="settings_card">
      {props.stack && <StackCard />}
      <Container 
        onClick      = {(e) => {onClick(e); e.stopPropagation(); document.body.click();}}
        expanded     = {exp}
        color        = {backgroundColor}
        hoverColor   = {hoverColor}
        grayOut      = {grayOut}
        height       = {height}
        width        = {width}
        isOver       = {Boolean(isOver)}
        isDragging   = {isDragging}
        ref          = {ref}
      >
        {exp ?
          <Body expanded={!exp} padding='1rem'>{expandedBody}</Body>
          :
          <Body expanded={exp}>
            {body}
            <Footer borderColor={hoverColor}>{footer}</Footer>
          </Body>
        }
      </Container>
      {(action) && 
        <ActionContainer onClick={onAction}>
          {action}
        </ActionContainer>
      }
    </PosContainer>
  );
}));